import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, X, FileText, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { value: "sales_return", label: "Sales Return", hint: "Goods returned by customer" },
  { value: "quantity_reduction", label: "Quantity Reduction", hint: "Fewer units than billed" },
  { value: "rate_correction", label: "Rate Correction", hint: "Wrong rate used in original" },
  { value: "wrong_gst_applied", label: "Wrong GST Applied", hint: "Tax rate / GSTIN error" },
  { value: "cancelled_order", label: "Cancelled Order", hint: "Full reversal — order voided" },
  { value: "other", label: "Other", hint: "Add details below" },
];

export default function CreditNoteDialog({ invoiceId, invoice, onClose, onCreated }) {
  const [mode, setMode] = useState("full");
  const [reason, setReason] = useState("sales_return");
  const [reasonNote, setReasonNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Partial mode: editable copy of items with reduced quantities
  const [partialItems, setPartialItems] = useState(
    (invoice?.items || []).map((it) => ({
      name: it.name || "",
      quantity: 0,
      max_quantity: Number(it.quantity) || 0,
      unit_price: Number(it.unit_price) || 0,
      gst_percent: Number(it.gst_percent) || 0,
      hsn_code: it.hsn_code || "",
    }))
  );

  const updatePartialQty = (idx, raw) => {
    const v = Math.max(0, Math.min(Number(raw) || 0, partialItems[idx].max_quantity));
    setPartialItems((prev) => prev.map((it, i) => (i === idx ? { ...it, quantity: v } : it)));
  };

  const partialTotal = partialItems.reduce((sum, it) => {
    const base = it.quantity * it.unit_price;
    return sum + base + (base * it.gst_percent) / 100;
  }, 0);

  const submit = async () => {
    if (mode === "partial" && partialItems.every((it) => it.quantity === 0)) {
      toast.error("Select at least one line item with quantity > 0");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        mode,
        reason,
        reason_note: reasonNote.trim(),
      };
      if (mode === "partial") {
        body.items = partialItems
          .filter((it) => it.quantity > 0)
          .map((it) => ({
            name: it.name,
            quantity: it.quantity,
            unit_price: it.unit_price,
            gst_percent: it.gst_percent,
            hsn_code: it.hsn_code,
          }));
      }
      const res = await axios.post(`${API}/builder/invoices/${invoiceId}/credit-note`, body, { withCredentials: true });
      toast.success("Credit Note draft created");
      onCreated?.(res.data.credit_note);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create Credit Note");
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(n || 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" onClick={onClose} data-testid="cn-dialog-backdrop">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="cn-dialog">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl">
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Issue Credit Note</h2>
              <p className="text-xs text-slate-500 mt-0.5">Against {invoice?.invoice_number} — {invoice?.customer_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="cn-dialog-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Mode toggle */}
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Mode</label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <button
                onClick={() => setMode("full")}
                className={`text-left rounded-xl border p-3 transition-all ${mode === "full" ? "border-blue-500 bg-blue-50 ring-2 ring-blue-100" : "border-slate-200 hover:border-slate-300"}`}
                data-testid="cn-mode-full"
              >
                <p className="text-sm font-semibold text-slate-900">Full Reversal</p>
                <p className="text-xs text-slate-500 mt-0.5">Reverses the entire invoice</p>
              </button>
              <button
                onClick={() => setMode("partial")}
                className={`text-left rounded-xl border p-3 transition-all ${mode === "partial" ? "border-blue-500 bg-blue-50 ring-2 ring-blue-100" : "border-slate-200 hover:border-slate-300"}`}
                data-testid="cn-mode-partial"
              >
                <p className="text-sm font-semibold text-slate-900">Partial</p>
                <p className="text-xs text-slate-500 mt-0.5">Reverse selected items / quantities</p>
              </button>
            </div>
          </div>

          {/* Partial line items editor */}
          {mode === "partial" && (
            <div data-testid="cn-partial-editor">
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Select Quantity to Reverse</label>
              <div className="mt-2 border border-slate-100 rounded-xl divide-y divide-slate-50 overflow-hidden">
                {partialItems.map((it, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 text-sm" data-testid={`cn-partial-row-${idx}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{it.name}</p>
                      <p className="text-xs text-slate-500">{fmt(it.unit_price)} / unit · {it.gst_percent}% GST · billed {it.max_quantity}</p>
                    </div>
                    <input
                      type="number"
                      min={0}
                      max={it.max_quantity}
                      step="0.01"
                      value={it.quantity}
                      onChange={(e) => updatePartialQty(idx, e.target.value)}
                      className="w-24 text-sm text-right border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid={`cn-partial-qty-${idx}`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-end mt-2 text-sm">
                <span className="text-slate-500">Reversal total:</span>
                <span className="ml-2 font-bold text-slate-900" data-testid="cn-partial-total">{fmt(partialTotal)}</span>
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Reason</label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {REASONS.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setReason(r.value)}
                  className={`text-left rounded-lg border p-2.5 transition-all ${reason === r.value ? "border-amber-400 bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid={`cn-reason-${r.value}`}
                >
                  <p className="text-xs font-semibold text-slate-900">{r.label}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{r.hint}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Reason note */}
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Additional Note <span className="text-slate-400 font-normal normal-case">(optional, max 500 chars)</span></label>
            <textarea
              value={reasonNote}
              onChange={(e) => setReasonNote(e.target.value.slice(0, 500))}
              placeholder="Internal note for your records (e.g., authorisation reference)"
              className="w-full mt-2 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows={2}
              data-testid="cn-reason-note"
            />
          </div>

          {/* Info banner */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex items-start gap-2 text-xs">
            <AlertTriangle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-blue-800">
              The Credit Note is created as <span className="font-semibold">draft</span>. You can review it next, then issue & download the PDF — the original invoice stays intact for accounting integrity.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-5 border-t border-slate-100 sticky bottom-0 bg-white rounded-b-2xl">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="cn-dialog-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="cn-dialog-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileText className="w-4 h-4 mr-1" />}
            Create Credit Note
          </Button>
        </div>
      </div>
    </div>
  );
}
